import React, { useLayoutEffect } from 'react'
import { omit } from 'lodash'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { User } from 'src/State/User/UserModel'
import { useUser, UserForm, usePresetUserCompany, useUserViewFormInitializer, useQntmInviteDialog } from 'src/Libs/service-dashboard'
import { useTypedSelector } from 'src/State/RootReducer'
import UserViewForm from './UserViewForm'
import { SERVICE_LEVELS } from 'src/Configuration'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useNavigate } from 'react-router-dom'

const useStyles = makeStyles()(theme => ({
  container: {
    paddingInline: theme.spacing(3),
  },
}))

interface UserViewDataProps {
  refreshUserData?: () => Promise<void>
  user?: User
  isEditing?: boolean
}
/**
 * Wrapper for user data form
 */
const UserViewData: React.FC<UserViewDataProps> = ({ user, refreshUserData, isEditing }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { initialValues: formValues, validationSchema, isUsernameValid } = useUserViewFormInitializer(user, isEditing)

  const { openConfirmationModal } = useModals()
  const openQntmInvitationDialog = useQntmInviteDialog()
  const serviceUser = useTypedSelector(state => state.user.userData)
  const { initialCompanyId = user?.company?.id } = usePresetUserCompany()

  const { createUser, updateUser, deleteUser, resendInvitationEmail, resendIpinEmail } = useUser({
    createdBy: serviceUser?.ID,
    onSuccess: refreshUserData,
  })

  const methods = useForm<UserForm>({
    defaultValues: formValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  })

  useLayoutEffect(() => {
    methods.reset(formValues)
  }, [formValues, methods])

  const onSave = async (
    values: UserForm,
    setFieldError: (fieldName: keyof UserForm, errorMessage: string) => void,
    sendIpin: boolean
  ): Promise<void> => {
    let request: Partial<UserForm> = values

    if (values.userLevel !== 'INSTALLER') {
      /** Don't send IPIN if user is not INSTALLER cause of BE validation. */
      request = omit(request, ['ipin', 'infinityCertified', 'qntmCertified'])
    }

    if (!SERVICE_LEVELS.includes(values.userLevel)) {
      /** Don't send coupon permission if user is not service or above cause of BE validation. */
      request = omit(request, ['couponPermission'])
    }

    if (user?.id && isEditing) {
      /** Don't send username if it was not changed cause of BE validation. */
      if (formValues.username === values.username) {
        request = omit(request, ['username'])
      }

      /** Don't send the company if it was not changed cause of BE requirments. */
      if (formValues.company === values.company) {
        request = omit(request, ['company'])
      }

      await updateUser({
        userId: user.id,
        locationId: user.contactData.ID ?? null,
        formValues: request,
        setFieldError,
        companyIdForPermissions: request.company ?? values.company,
        currentCompanyHasShop: user.company?.shopPermission,
        initialCompanyId,
        initialUseShop: formValues.useShop,
      })
    } else {
      await createUser(request as UserForm, sendIpin).then(() => {
        if (initialCompanyId) {
          navigate(`/overview/companies/${initialCompanyId}`)
        }
      })
    }
  }

  const getConfirmText = (userLevel: string, isValidEmail: boolean): string => {
    if (userLevel === 'E3DC_SERVICE') {
      /* only warnings for service users if the username is not an email */
      return t('service-dashboard.users.user-name-warning.service-email')
    }
    if (userLevel === 'PARTNER') {
      return t('service-dashboard.users.user-name-warning.partner-email')
    } else {
      if (!isValidEmail) {
        return t('service-dashboard.users.user-name-warning.partner-email')
      }

      return t('service-dashboard.users.user-name-warning.partner-name')
    }
  }

  const setFieldError = (fieldName: keyof UserForm, errorMessage: string): void => {
    methods.setError(fieldName, { type: 'server', message: errorMessage })
  }

  const onSubmit = async (values: UserForm): Promise<void> => {
    const validationResult = await isUsernameValid(values.username, values.userLevel, values.company, values.firstName, values.lastName)

    if (values.qntmCertified && !isEditing) {
      const validQntmIpin = values.ipin?.endsWith('50')

      openQntmInvitationDialog({
        hasQntmIpin: validQntmIpin,
        onSave: () => {
          onSave(values, setFieldError, true)
        },
        onCancel: () => {
          onSave(values, setFieldError, false)
        },
      })
    } else if (validationResult.isUsernameValidCompanyEmail && validationResult.isUsernameValidFirstAndLastName) {
      onSave(values, setFieldError, true)
    } else {
      openConfirmationModal({
        title: t('common.attention'),
        text: getConfirmText(values.userLevel, validationResult.isUsernameValidCompanyEmail),
        onConfirm: onClose => {
          onSave(values, setFieldError, true)
          onClose()
        },
        buttonText: [t('common.cancel'), t('common.save')],
      })
    }
  }

  return (
    <Box className={classes.container}>
      <FormProvider {...methods}>
        <UserViewForm
          initialValues={formValues}
          isEditing={isEditing}
          deleteUser={deleteUser}
          resendInvitationEmail={resendInvitationEmail}
          resendIpinEmail={resendIpinEmail}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </Box>
  )
}

export default UserViewData
